import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { SignaturePad } from "../components/SignaturePad";

const VARIANTS = {
  employee: {
    stepTitle: "Employee Handbook (full text)",
    aboutTitle: "About the Employee Handbook",
    title: "Employee Handbook",
    subtitle: "Silver Home Care policies, expectations, and resources for every Direct Care Worker.",
    backTo: "/orientation",
    backLabel: "Back to orientation",
    testid: "employee-handbook",
  },
  client: {
    stepTitle: "SHC Client Handbook (client-facing)",
    title: "Client Handbook",
    subtitle: "The client-facing handbook given to every client at admission — services, rights, contacts, and policies.",
    backTo: "/clients",
    backLabel: "Back to clients",
    testid: "client-handbook",
  },
  reference: {
    stepTitle: "Quick Reference Sheet",
    title: "Caregiver Quick Reference",
    subtitle: "The daily cheat sheet — call-out chain, emergencies, daily requirements, protective agencies, and key laws.",
    backTo: "/",
    backLabel: "Back to dashboard",
    testid: "quick-reference",
  },
};

function parseBlocks(text) {
  const blocks = [];
  let buf = [];
  let atStart = true;
  const flush = () => {
    if (buf.length) { blocks.push({ type: "p", text: buf.join(" ") }); buf = []; }
  };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); atStart = true; continue; }
    if (/^\d{1,3}$/.test(line)) { continue; }
    if (/^\d+\.\d+\s+\S/.test(line)) { flush(); blocks.push({ type: "h3", text: line }); atStart = true; continue; }
    if (/^\d+\.\s+[A-Z]/.test(line)) { flush(); blocks.push({ type: "h2", text: line }); atStart = true; continue; }
    if (/^[A-Z0-9 &'\-–—|:®©.]{8,}$/.test(line) && /[A-Z]/.test(line) && !/[a-z]/.test(line)) {
      flush(); blocks.push({ type: "h3", text: line }); atStart = true; continue;
    }
    if (atStart && line.length <= 60 && !/[.,;:!?)]$/.test(line) && !/^[a-z]/.test(line)) {
      flush(); blocks.push({ type: "h3", text: line }); atStart = true; continue;
    }
    buf.push(line);
    atStart = false;
  }
  flush();
  return blocks;
}

export default function HandbookPage({ variant }) {
  const v = VARIANTS[variant] || VARIANTS.employee;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orientation")
    .then((r) => {
      setData(r.data);
      if (!(r.data.steps || []).some((s) => s.title === v.stepTitle)) setMissing(true);
    })
    .catch((e) => { toast.error(formatApiError(e)); setMissing(true); });
  useEffect(() => { load(); }, [v.stepTitle]);

  const step = data ? (data.steps || []).find((s) => s.title === v.stepTitle) : null;
  const about = v.aboutTitle && data ? (data.steps || []).find((s) => s.title === v.aboutTitle) : null;
  const curriculum = (data?.steps || []).filter((s) => s.kind === "curriculum");
  const allDone = curriculum.length > 0 && curriculum.every((s) => s.done_at);

  const signAck = async () => {
    if (!sig) { toast.error("Please draw your signature"); return; }
    setBusy(true);
    try {
      await api.post("/orientation/acknowledge", { signature_png: sig });
      toast.success("Handbook acknowledged — filed on your record");
      setSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const blocks = useMemo(() => (step ? parseBlocks(step.content || "") : []), [step]);

  return (
    <div className="mx-auto max-w-3xl" data-testid={`${v.testid}-page`}>
      <Link to={v.backTo} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-stone-500 hover:text-primary" data-testid={`${v.testid}-back`}>
        <ArrowLeft size={14} /> {v.backLabel}
      </Link>

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-primary shadow-sm" data-testid={`${v.testid}-cover`}>
        <div className="flex flex-col items-start gap-4 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Silver Home Care</p>
            <h1 className="mt-1 font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl">{v.title}</h1>
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/80">{v.subtitle}</p>
            {step && (
              <p className="mt-3 text-xs text-white/60" data-testid={`${v.testid}-version`}>
                Version {step.version} · Updated {String(step.updated_at || "").slice(0, 10)}
              </p>
            )}
          </div>
          <img src="/shc-logo.png" alt="" className="h-20 w-auto rounded-xl bg-white/90 p-2" />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-full border-stone-300" data-testid={`${v.testid}-print`}>
          <Printer size={15} className="mr-1.5" /> Print / save PDF
        </Button>
        {isAdmin && (
          <Link to="/orientation" className="inline-flex h-8 items-center gap-1.5 rounded-full border border-stone-300 px-3 text-xs font-medium text-stone-600 hover:bg-stone-900 hover:text-white" data-testid={`${v.testid}-edit-link`}>
            <PencilSimple size={13} /> Edit content in Orientation
          </Link>
        )}
      </div>

      {!step && !missing && (
        <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      )}
      {missing && (
        <div className="mt-6 rounded-xl border border-dashed border-stone-300 bg-white p-10 text-center" data-testid={`${v.testid}-missing`}>
          <p className="font-heading text-lg font-semibold text-stone-700">Handbook not found</p>
          <p className="mt-1 text-sm text-stone-500">The handbook reference isn't set up yet. {isAdmin ? "Add it as a reference in Orientation." : "Please check with the office."}</p>
        </div>
      )}

      {step && (
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-8 shadow-sm sm:p-12" data-testid={`${v.testid}-document`}>
          {blocks.map((b, i) => {
            if (b.type === "h2") {
              return (
                <h2 key={i} className="mb-3 mt-10 border-b-2 border-primary/20 pb-2 font-heading text-2xl font-semibold tracking-tight text-stone-900 first:mt-0" data-testid={`${v.testid}-section-${i}`}>
                  {b.text}
                </h2>
              );
            }
            if (b.type === "h3") {
              return (
                <h3 key={i} className="mb-2 mt-7 font-heading text-base font-semibold uppercase tracking-wide text-primary" data-testid={`${v.testid}-section-${i}`}>
                  {b.text}
                </h3>
              );
            }
            return <p key={i} className="mb-4 text-sm leading-relaxed text-stone-700" data-testid={`${v.testid}-para-${i}`}>{b.text}</p>;
          })}
        </div>
      )}

      {variant === "employee" && about && (
        <div className="mt-6 rounded-2xl border-2 border-primary/30 bg-white p-8 shadow-sm" data-testid="employee-handbook-attestation">
          <h2 className="font-heading text-xl font-semibold text-stone-900">Handbook acknowledgment</h2>
          <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="employee-handbook-attestation-text">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{about.content}</p>
          </div>
          {data?.acknowledged ? (
            <p className="mt-4 flex items-center gap-2 text-sm font-medium text-[#1e8449]" data-testid="employee-handbook-ack-done">
              ✓ Acknowledged {String(data.ack?.signed_at).slice(0, 10)} — filed on your record
            </p>
          ) : allDone ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-stone-600">Sign below to acknowledge you have received, read, and understand the Employee Handbook.</p>
              <SignaturePad onChange={setSig} />
              <div className="flex justify-end">
                <Button onClick={signAck} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="employee-handbook-ack-sign">
                  {busy ? "Signing…" : "Sign & acknowledge handbook"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="employee-handbook-ack-locked">
              <p className="text-sm text-stone-600">The handbook attestation unlocks once all orientation parts are checked off.</p>
              <Link to="/orientation" className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-stone-900" data-testid="employee-handbook-go-orientation">
                Go to Orientation
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
